import React, { useState } from "react";
import { Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PortalStyleClassic = ({ modules, onNavigate }) => {
    const [hoveredModule, setHoveredModule] = useState(null);

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gridTemplateRows: "repeat(2, 1fr)",
                gap: "16px", // Increased gap
                height: "100%",
                padding: "8px",
            }}
        >
            {modules.map((mod) => (
                <div
                    key={mod.key}
                    style={{
                        position: "relative",
                        background: "#fff",
                        borderRadius: "16px",
                        // border: moved to overlay
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: hoveredModule === mod.key ? "0 12px 24px rgba(0,0,0,0.1)" : "0 2px 8px rgba(0,0,0,0.04)",
                        cursor: "default",
                    }}
                    onMouseEnter={() => setHoveredModule(mod.key)}
                    onMouseLeave={() => setHoveredModule(null)}
                >
                    {/* Background decoration to reduce empty feel */}
                    <div
                        style={{
                            position: "absolute",
                            top: "-20%",
                            right: "-20%",
                            width: "150px",
                            height: "150px",
                            background: mod.color,
                            borderRadius: "50%",
                            opacity: 0.3,
                            filter: "blur(40px)",
                            transition: "all 0.5s ease",
                            transform: hoveredModule === mod.key ? "scale(1.5)" : "scale(1)",
                        }}
                    />

                    {/* Main Content */}
                    <div
                        style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "24px",
                            transition: "all 0.3s ease",
                            opacity: hoveredModule === mod.key ? 0.05 : 1, // Almost disappear
                            transform: hoveredModule === mod.key ? "scale(0.9)" : "scale(1)",
                            zIndex: 1,
                        }}
                    >
                        <div
                            style={{
                                marginBottom: "20px",
                                color: mod.borderColor,
                                background: mod.color,
                                padding: "20px",
                                borderRadius: "24px", // Squircle
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {mod.icon}
                        </div>
                        <Title level={4} style={{ margin: "0 0 8px 0", textAlign: "center", color: "#262626" }}>
                            {mod.title}
                        </Title>
                        <Text type="secondary" style={{ textAlign: "center", fontSize: "13px", lineHeight: "1.5", maxWidth: "80%" }}>
                            {mod.description}
                        </Text>
                    </div>

                    {/* Hover Menu (Slide Up) */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.98)",
                            backdropFilter: "blur(10px)",
                            padding: "16px",
                            display: "flex",
                            flexDirection: "column",
                            transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)", // Spring-like
                            transform: hoveredModule === mod.key ? "translateY(0)" : "translateY(100%)",
                            opacity: hoveredModule === mod.key ? 1 : 0,
                            zIndex: 10,
                            overflowY: "auto",
                        }}
                    >
                        <div
                            style={{
                                borderBottom: `2px solid ${mod.borderColor}`,
                                marginBottom: "12px",
                                paddingBottom: "8px",
                                fontWeight: 700,
                                fontSize: "14px",
                                color: "#262626",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>{mod.title}</span>
                            <span style={{ color: mod.borderColor }}>{mod.icon}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {mod.items.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigate(item.path);
                                    }}
                                    className="portal-link-item"
                                    style={{
                                        padding: "12px 16px",
                                        borderRadius: "8px",
                                        background: mod.color,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        fontSize: "14px",
                                        border: "1px solid transparent",
                                        transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "#fff";
                                        e.currentTarget.style.borderColor = mod.borderColor;
                                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                                        e.currentTarget.style.transform = "translateX(4px)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = mod.color;
                                        e.currentTarget.style.borderColor = "transparent";
                                        e.currentTarget.style.boxShadow = "none";
                                        e.currentTarget.style.transform = "translateX(0)";
                                    }}
                                >
                                    <span style={{ fontSize: "18px", color: mod.borderColor }}>{item.icon}</span>
                                    <span style={{ fontWeight: 500, color: "#262626" }}>{item.label}</span>
                                    <RightOutlined style={{ marginLeft: "auto", fontSize: "12px", opacity: 0.4 }} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Border Overlay: Fixed border clipping issue */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: "16px",
                            border: `1px solid ${hoveredModule === mod.key ? mod.borderColor : "#f0f0f0"}`,
                            pointerEvents: "none",
                            zIndex: 20, // Above everything
                            transition: "border-color 0.3s ease",
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

export default PortalStyleClassic;
